import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import DaumPostcode from 'react-daum-postcode';
import JobLayout from '../../media/joblayout/JobLayout';
import JobApplyConsentModal from './JobApplyConsentModal';
import './jobapply.css';
import { getApiBaseUrl } from '../../../api/services/utils/apiConfig';

const JobApply = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [step, setStep] = useState(1);
  const [activeTab, setActiveTab] = useState('basic');
  const [isPostOpen, setIsPostOpen] = useState(false);
  const addressDetailRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    phone1: '010',
    phone2: '',
    phone3: '',
    email: '',
    emailConfirm: '',
    password: '',
    passwordConfirm: '',
    privacyAgreed: false,
    gender: '',
    birthDate: '',
    photo: null,
    nationality: '대한민국',
    postcode: '',
    address: '',
    addressDetail: '',
    career: '',
    license: '',
    freightLicense: '',      // 화물운송종사자격증 보유 여부
    hasVehicle: '',           // 차량 소지 여부
    vehicleModel: '',         // 소지 차량 차종
    hasVehicleNumber: '',     // 배넘버 여부
    otherNotes: '',           // 기타사항
  });
  const [careers, setCareers] = useState([
    { employmentType: '', companyName: '', employeeCount: '', employmentStatus: '', startDate: '', endDate: '', assignedTasks: '', leaveReason: '' },
  ]);

  useEffect(() => {
    const fetchJobInfo = async () => {
      try {
        const response = await axios.get(`${getApiBaseUrl()}/jobs/${id}`);
        const jobData = response.data?.data || response.data;
        setJob(jobData);
      } catch (error) {
        console.error("공고 정보를 가져오는데 실패했습니다:", error);
      }
    };
    if (id) fetchJobInfo();
  }, [id]);

  // 임시 저장된 데이터 불러오기
  useEffect(() => {
    if (!id) return;
    try {
      const saved = localStorage.getItem(`job-apply-draft-${id}`);
      if (saved) {
        const data = JSON.parse(saved);
        if (data.jobId === id && window.confirm('임시 저장된 지원서가 있습니다. 불러오시겠습니까?')) {
          const { jobId, jobTitle, savedAt, careers: savedCareers, ...rest } = data;
          setFormData((prev) => ({ ...prev, ...rest }));
          if (savedCareers?.length) setCareers(savedCareers);
          setStep(2);
          setActiveTab('basic');
        }
      }
    } catch (e) {
      /* 무시 */
    }
  }, [id]);

  const getFullPhone = () => {
    return `${formData.phone1}-${formData.phone2}-${formData.phone3}`.trim();
  };

  const isBasicTabComplete = () => {
    return (
      !!formData.gender &&
      !!formData.birthDate &&
      !!formData.nationality &&
      !!(formData.postcode && formData.address)
    );
  };

  const validateBasicTab = () => {
    if (!formData.gender) {
      alert('성별을 선택해주세요.');
      return false;
    }
    if (!formData.birthDate) {
      alert('생년월일을 입력해주세요.');
      return false;
    }
    if (!formData.nationality) {
      alert('국적을 선택해주세요.');
      return false;
    }
    if (!formData.postcode || !formData.address) {
      alert('주소를 입력해주세요.');
      return false;
    }
    return true;
  };

  const updateCareer = (idx, field, value) => {
    setCareers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'employmentStatus' && value === 'current') next[idx].endDate = '';
      return next;
    });
  };

  const addCareer = () => {
    setCareers((prev) => [...prev, { employmentType: '', companyName: '', employeeCount: '', employmentStatus: '', startDate: '', endDate: '', assignedTasks: '', leaveReason: '' }]);
  };

  const removeCareer = (idx) => {
    if (careers.length <= 1) return;
    setCareers((prev) => prev.filter((_, i) => i !== idx));
  };

  const hasAnyCareerInput = () => {
    return careers.some(
      (c) =>
        c.employmentType ||
        c.companyName ||
        c.employeeCount ||
        c.startDate ||
        c.endDate ||
        c.assignedTasks ||
        c.leaveReason
    );
  };

  const canGoToTab = (tab) => {
    if (tab === 'basic') return true;
    if (tab === 'career') return isBasicTabComplete();
    if (tab === 'license') return hasAnyCareerInput(); // 경력사항 하나라도 입력 시 활성화
    return false;
  };

  const saveDraftSilently = async () => {
    try {
      const payload = buildApplyPayload(false);
      await axios.post(`${getApiBaseUrl()}/user/apply/draft`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (err) {
      console.error('탭 전환 시 저장 실패:', err);
    }
  };

  const handleTabClick = async (tab) => {
    if (activeTab === tab) return;
    if (tab === 'basic') {
      await saveDraftSilently();
      setActiveTab('basic');
      return;
    }
    if (tab === 'license' && !canGoToTab('license')) return;
    if (tab === 'career' && !validateBasicTab()) return;
    await saveDraftSilently();
    setActiveTab(tab);
  };

  const validateStep1 = () => {
    if (!formData.privacyAgreed) {
      alert('개인정보 수집·이용에 동의해주세요.');
      return false;
    }
    if (formData.email !== formData.emailConfirm) {
      alert('이메일이 일치하지 않습니다.');
      return false;
    }
    if (formData.password && formData.password !== formData.passwordConfirm) {
      alert('비밀번호가 일치하지 않습니다.');
      return false;
    }
    const phone = getFullPhone();
    if (!formData.phone2 || !formData.phone3) {
      alert('휴대전화를 입력해주세요.');
      return false;
    }
    return true;
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    if (validateStep1()) {
      setStep(2);
      setActiveTab('basic');
    }
  };

  const handlePostcodeComplete = (data) => {
    setFormData((prev) => ({
      ...prev,
      postcode: data.zonecode,
      address: data.address,
    }));
    setIsPostOpen(false);
    setTimeout(() => {
      addressDetailRef.current?.focus();
    }, 100);
  };

  const handleTempSave = (e) => {
    e.preventDefault();
    try {
      const saveData = {
        jobId: id,
        jobTitle: job?.title,
        ...formData,
        careers,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(`job-apply-draft-${id}`, JSON.stringify(saveData));
      alert('임시 저장되었습니다.\n나중에 이 페이지에서 다시 작성하실 수 있습니다.');
    } catch (err) {
      alert('임시 저장에 실패했습니다.');
    }
  };

  const buildApplyPayload = (isFinal = false) => {
    const phone = getFullPhone();
    const address = [formData.postcode, formData.address, formData.addressDetail].filter(Boolean).join(' ');
    const payload = new FormData();
    payload.append("jobId", id);
    payload.append("name", formData.name.replace(/\s/g, ''));
    payload.append("phone", phone);
    payload.append("email", formData.email);
    if (formData.password) payload.append("password", formData.password);
    if (formData.birthDate) payload.append("age", formData.birthDate);
    if (formData.gender) payload.append("gender", formData.gender);
    if (address) payload.append("address", address);
    if (formData.photo) payload.append("photo", formData.photo);
    const careerStr = careers.length ? JSON.stringify(careers) : formData.career || '';
    if (careerStr) payload.append("career", careerStr);
    if (formData.license) payload.append("license", formData.license);
    if (formData.freightLicense) payload.append("freightLicense", formData.freightLicense);
    if (formData.hasVehicle) payload.append("hasVehicle", formData.hasVehicle);
    if (formData.vehicleModel) payload.append("vehicleModel", formData.vehicleModel);
    if (formData.hasVehicleNumber) payload.append("hasVehicleNumber", formData.hasVehicleNumber);
    if (formData.otherNotes) payload.append("otherNotes", formData.otherNotes);
    payload.append("isFinal", isFinal ? "true" : "false");
    return payload;
  };

  const handleNextTabSave = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;
    if (activeTab === 'basic' && !validateBasicTab()) return;

    try {
      const payload = buildApplyPayload(false);
      await axios.post(`${getApiBaseUrl()}/user/apply/draft`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('저장되었습니다.');
      if (activeTab === 'basic') setActiveTab('career');
      else if (activeTab === 'career') setActiveTab('license');
    } catch (error) {
      const msg = error.response?.data;
      const errStr = typeof msg === 'string' ? msg : msg?.message || '';
      alert(errStr || '저장 중 오류가 발생했습니다.');
    }
  };

  const validateLicenseTab = () => {
    if (!formData.freightLicense) {
      alert('화물운송종사자격증 보유 여부를 선택해주세요.');
      return false;
    }
    if (!formData.hasVehicle) {
      alert('차량 소지 여부를 선택해주세요.');
      return false;
    }
    if (formData.hasVehicle === 'yes' && !formData.vehicleModel?.trim()) {
      alert('소지 차량 차종을 입력해주세요.');
      return false;
    }
    if (!formData.hasVehicleNumber) {
      alert('배넘버 여부를 선택해주세요.');
      return false;
    }
    return true;
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;
    if (!validateLicenseTab()) return;

    try {
      const payload = buildApplyPayload(true);
      const response = await axios.post(`${getApiBaseUrl()}/user/apply/draft`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data) {
        alert('지원이 정상적으로 완료되었습니다!\n지원해주셔서 진심으로 감사드립니다.\n\n지원 결과는 개별적으로 안내드릴 예정입니다.');
        navigate(`/careers/${id}`);
      }
    } catch (error) {
      const msg = error.response?.data;
      const errStr = typeof msg === 'string' ? msg : msg?.message || '';
      alert(errStr || '지원 중 오류가 발생했습니다.\n잠시 후 다시 시도해 주세요.');
    }
  };

  const pageTitle = job?.title ? `${job.title} 지원 - 주식회사 가까운물류` : '지원하기 - 주식회사 가까운물류';

  return (
    <JobLayout>
      <Helmet><title>{pageTitle}</title></Helmet>

      <div className="job-apply-v2">
        <div className="job-apply-v2-container">
          <h1 className="job-apply-v2-title">지원서 작성</h1>

          <div className="job-apply-v2-job-info">
            <div className="job-apply-v2-row">
              <label>공고명</label>
              <div className="job-apply-v2-value">{job?.title || '-'}</div>
            </div>
          </div>

          {step === 1 && (
          <div className="job-apply-v2-privacy">
            <div className="job-apply-v2-row job-apply-v2-row-privacy">
              <label>개인정보 수집·이용 동의</label>
              <div className="job-apply-v2-privacy-right">
                <button
                  type="button"
                  className="job-apply-v2-btn-outline"
                  disabled={formData.privacyAgreed}
                  onClick={() => setShowConsentModal(true)}
                >
                  {formData.privacyAgreed ? '확인 완료' : '내용확인'}
                </button>
                {!formData.privacyAgreed && (
                  <span className="job-apply-v2-privacy-hint-inline">※ 필수 항목에 동의해주세요.</span>
                )}
              </div>
            </div>
          </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (step === 1) handleNextStep(e);
              else if (activeTab === 'license') handleApplySubmit(e);
              else handleNextTabSave(e);
            }}
            className="job-apply-v2-form"
          >
            {step === 1 && (
              <>
                <div className="job-apply-v2-row">
              <label>성명 <span className="required">*</span></label>
              <div className="job-apply-v2-input-wrap">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
                <p className="job-apply-v2-field-hint">※ 공백 없이 입력하세요</p>
              </div>
            </div>

            <div className="job-apply-v2-row">
              <label>휴대전화 <span className="required">*</span></label>
              <div className="job-apply-v2-input-wrap">
                <div className="job-apply-v2-phone">
                  <input
                    type="text"
                    value={formData.phone1}
                    onChange={(e) => setFormData({ ...formData, phone1: e.target.value })}
                    maxLength={4}
                    className="phone-part"
                  />
                  <span className="phone-dash">-</span>
                  <input
                    type="text"
                    value={formData.phone2}
                    onChange={(e) => setFormData({ ...formData, phone2: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    maxLength={4}
                    className="phone-part"
                  />
                  <span className="phone-dash">-</span>
                  <input
                    type="text"
                    value={formData.phone3}
                    onChange={(e) => setFormData({ ...formData, phone3: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    maxLength={4}
                    className="phone-part"
                  />
                </div>
                <p className="job-apply-v2-field-hint">※ 숫자만 입력 (예: 010-0000-0000)</p>
              </div>
            </div>

            <div className="job-apply-v2-row">
              <label>이메일 <span className="required">*</span></label>
              <div className="job-apply-v2-input-wrap job-apply-v2-grouped-wrap">
                <div className="job-apply-v2-grouped-input">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="이메일을 입력해주세요."
                    required
                  />
                  <input
                    type="email"
                    value={formData.emailConfirm}
                    onChange={(e) => setFormData({ ...formData, emailConfirm: e.target.value })}
                    placeholder="이메일 확인을 위해 다시 한번 입력해주세요."
                    required
                  />
                </div>
              </div>
            </div>

            <div className="job-apply-v2-row">
              <label>비밀번호 <span className="required">*</span></label>
              <div className="job-apply-v2-input-wrap job-apply-v2-grouped-wrap">
                <div className="job-apply-v2-grouped-input">
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="비밀번호를 입력해주세요. (8~20자, 영문·숫자·특수문자 조합)"
                  />
                  <input
                    type="password"
                    value={formData.passwordConfirm}
                    onChange={(e) => setFormData({ ...formData, passwordConfirm: e.target.value })}
                    placeholder="비밀번호 확인을 위해 다시 한번 입력해주세요."
                  />
                </div>
              </div>
            </div>

                <div className="job-apply-v2-footer">
                  <button type="submit" className="job-apply-v2-btn-submit">
                    다음
                  </button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="job-apply-v2-tabs">
                  <button
                    type="button"
                    className={`job-apply-v2-tab ${activeTab === 'basic' ? 'active' : ''}`}
                    onClick={() => handleTabClick('basic')}
                  >
                    1. 기본정보
                  </button>
                  <button
                    type="button"
                    className={`job-apply-v2-tab ${activeTab === 'career' ? 'active' : ''} ${!canGoToTab('career') ? 'disabled' : ''}`}
                    onClick={() => handleTabClick('career')}
                    disabled={!canGoToTab('career')}
                  >
                    2. 경력사항
                  </button>
                  <button
                    type="button"
                    className={`job-apply-v2-tab ${activeTab === 'license' ? 'active' : ''} ${!canGoToTab('license') ? 'disabled' : ''}`}
                    onClick={() => handleTabClick('license')}
                    disabled={!canGoToTab('license')}
                  >
                    3. 자격/기타
                  </button>
                </div>

                {activeTab === 'basic' && (
                  <div className="job-apply-v2-tab-panel">
                    <div className="job-apply-v2-section job-apply-v2-section-basic">
                      <div className="job-apply-v2-section-header">
                        <h3 className="job-apply-v2-section-title">기본정보</h3>
                        <span className="job-apply-v2-required-hint">* 필수항목</span>
                      </div>
                      <div className="job-apply-v2-row job-apply-v2-row-basic-label">
                          <label>지원자</label>
                          <div className="job-apply-v2-value job-apply-v2-prefilled">{formData.name || '-'}</div>
                        </div>
                      <div className="job-apply-v2-section-inner">
                      <div className="job-apply-v2-basic-body">
                        
                        <div className="job-apply-v2-basic-second-row">
                          <div className="job-apply-v2-inline-field">
                            <label><span className="required">*</span> 성별</label>
                            <div className="job-apply-v2-segmented">
                              <button
                                type="button"
                                className={`job-apply-v2-segmented-btn ${formData.gender === 'male' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, gender: 'male' })}
                              >
                                남
                              </button>
                              <button
                                type="button"
                                className={`job-apply-v2-segmented-btn ${formData.gender === 'female' ? 'active' : ''}`}
                                onClick={() => setFormData({ ...formData, gender: 'female' })}
                              >
                                여
                              </button>
                            </div>
                          </div>
                          <div className="job-apply-v2-inline-field">
                            <label><span className="required">*</span> 생년월일</label>
                            <input
                              type="date"
                              value={formData.birthDate}
                              onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                              className="job-apply-v2-date-input"
                            />
                          </div>
                        </div>
                      </div>
                      </div>
                    </div>
                    <hr className="job-apply-v2-divider" />
                    <div className="job-apply-v2-row job-apply-v2-row-basic-label">
                          <label>지원 분야</label>
                          <div className="job-apply-v2-value job-apply-v2-prefilled">{job?.title || '-'}</div>
                        </div>
                    <hr className="job-apply-v2-divider" />

                    <div className="job-apply-v2-section job-apply-v2-section-personal">
                      <div className="job-apply-v2-section-header">
                        <h3 className="job-apply-v2-section-title">인적사항</h3>
                        <span className="job-apply-v2-required-hint">* 필수항목</span>
                      </div>
                      <div className="job-apply-v2-section-inner">
                        <div className="job-apply-v2-personal-photo">
                          <div className="job-apply-v2-photo-preview">
                            <input
                              type="file"
                              accept="image/*"
                              id="photo-upload"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  setFormData({ ...formData, photo: e.target.files[0] });
                                }
                              }}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="photo-upload" className="job-apply-v2-photo-frame">
                              {formData.photo ? (
                                <img src={URL.createObjectURL(formData.photo)} alt="지원서 사진" />
                              ) : (
                                <span className="job-apply-v2-photo-placeholder">+</span>
                              )}
                            </label>
                          </div>
                          <div className="job-apply-v2-photo-actions">
                            <p className="job-apply-v2-photo-hint">지원서 사진등록 (160px x 200px 또는 4:5 비율)</p>
                            <div className="job-apply-v2-photo-btns">
                              <label htmlFor="photo-upload" className="job-apply-v2-btn-photo-reg">
                                사진등록
                              </label>
                              <button
                                type="button"
                                className="job-apply-v2-btn-photo-del"
                                onClick={() => setFormData({ ...formData, photo: null })}
                                disabled={!formData.photo}
                              >
                                삭제
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <hr className="job-apply-v2-divider" />
                      <div className="job-apply-v2-section-inner">
                        <div className="job-apply-v2-row job-apply-v2-row-thin">
                          <label><span className="required">*</span> 국적</label>
                          <div className="job-apply-v2-input-wrap">
                            <select
                              value={formData.nationality || '대한민국'}
                              onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                              className="job-apply-v2-select"
                            >
                              <option value="대한민국">대한민국</option>
                              <option value="기타">기타</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <hr className="job-apply-v2-divider" />
                      <div className="job-apply-v2-section-inner">
                        <div className="job-apply-v2-row job-apply-v2-row-thin job-apply-v2-row-address">
                          <label><span className="required">*</span> 주소</label>
                          <div className="job-apply-v2-address-wrap">
                            <div className="job-apply-v2-address-search">
                              <button type="button" className="job-apply-v2-btn-postcode" onClick={() => setIsPostOpen(true)}>
                                우편번호
                              </button>
                              {formData.postcode && (
                                <span className="job-apply-v2-postcode-display">
                                  {formData.postcode}
                                  <button type="button" className="job-apply-v2-postcode-clear" onClick={() => setFormData({ ...formData, postcode: '', address: '' })} aria-label="우편번호 삭제">×</button>
                                </span>
                              )}
                            </div>
                            <input
                              type="text"
                              value={formData.address}
                              readOnly
                              placeholder="주소를 검색해주세요"
                              onClick={() => setIsPostOpen(true)}
                              className="job-apply-v2-address-input"
                            />
                            <input
                              ref={addressDetailRef}
                              type="text"
                              value={formData.addressDetail}
                              onChange={(e) => setFormData({ ...formData, addressDetail: e.target.value })}
                              placeholder="상세 주소를 입력해주세요"
                              className="job-apply-v2-address-detail"
                            />
                          </div>
                        </div>
                      </div>
                      {isPostOpen && (
                        <>
                          <div className="job-apply-v2-postcode-overlay" onClick={() => setIsPostOpen(false)} />
                          <div className="job-apply-v2-postcode-modal">
                            <button type="button" className="job-apply-v2-postcode-close" onClick={() => setIsPostOpen(false)}>닫기</button>
                            <DaumPostcode
                              onComplete={handlePostcodeComplete}
                              autoClose
                              style={{ width: '500px', height: '550px' }}
                            />
                          </div>
                        </>
                      )}
                      <hr className="job-apply-v2-divider" />
                      <div className="job-apply-v2-section-inner">
                        <div className="job-apply-v2-row job-apply-v2-row-thin">
                          <label><span className="required">*</span> 연락처</label>
                          <div className="job-apply-v2-contact-wrap">
                            <div className="job-apply-v2-contact-item">
                              <span className="job-apply-v2-contact-label">이메일주소</span>
                              <span className="job-apply-v2-contact-value">{formData.email || '-'}</span>
                            </div>
                            <div className="job-apply-v2-contact-item">
                              <span className="job-apply-v2-contact-label">핸드폰번호</span>
                              <span className="job-apply-v2-contact-value">{getFullPhone() || '-'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'career' && (
                  <div className="job-apply-v2-tab-panel job-apply-v2-career-panel">
                    <div className="job-apply-v2-career-header">
                        <h3 className="job-apply-v2-career-title">경력사항</h3>
                        <span className="job-apply-v2-required-hint">* 필수항목</span>
                      </div>
                    <div className="job-apply-v2-tab-inner">
                      {careers.map((c, idx) => {
                        const fieldDisabled = !c.employmentType;
                        return (
                        <div key={idx} className="job-apply-v2-career-block">
                          <div className="job-apply-v2-career-block-header">
                            <label className="job-apply-v2-career-block-label">운송경력</label>
                            <button type="button" className="job-apply-v2-career-remove" onClick={() => removeCareer(idx)} disabled={careers.length <= 1}>[-]</button>
                          </div>
                          <div className="job-apply-v2-career-fields">
                            <div className="job-apply-v2-career-row">
                              <label><span className="required">*</span> 고용형태</label>
                              <select value={c.employmentType} onChange={(e) => updateCareer(idx, 'employmentType', e.target.value)}>
                                <option value="">선택</option>
                                <option value="정규직">정규직</option>
                                <option value="계약직">계약직</option>
                                <option value="아르바이트">아르바이트</option>
                                <option value="위촉직">위촉직</option>
                                <option value="기타">기타</option>
                              </select>
                            </div>
                            <div className="job-apply-v2-career-row job-apply-v2-career-row-split">
                              <div className="job-apply-v2-career-field">
                                <label><span className="required">*</span> 회사명</label>
                                <input type="text" value={c.companyName} onChange={(e) => updateCareer(idx, 'companyName', e.target.value)} placeholder="회사명" disabled={fieldDisabled} />
                              </div>
                              <div className="job-apply-v2-career-field job-apply-v2-career-field-sm">
                                <label>직원수</label>
                                <div className="job-apply-v2-career-unit-wrap">
                                  <input type="text" inputMode="numeric" value={c.employeeCount} onChange={(e) => updateCareer(idx, 'employeeCount', e.target.value.replace(/\D/g, ''))} placeholder="0" disabled={fieldDisabled} />
                                  <span className="job-apply-v2-career-unit">명</span>
                                </div>
                              </div>
                            </div>
                            <div className="job-apply-v2-career-row">
                              <label><span className="required">*</span> 근무기간</label>
                              <div className="job-apply-v2-career-period-wrap">
                                <div className="job-apply-v2-segmented">
                                  <button type="button" className={`job-apply-v2-segmented-btn ${!fieldDisabled && c.employmentStatus === 'current' ? 'active' : ''}`} onClick={() => !fieldDisabled && updateCareer(idx, 'employmentStatus', 'current')} disabled={fieldDisabled}>재직중</button>
                                  <button type="button" className={`job-apply-v2-segmented-btn ${!fieldDisabled && c.employmentStatus === 'resigned' ? 'active' : ''}`} onClick={() => !fieldDisabled && updateCareer(idx, 'employmentStatus', 'resigned')} disabled={fieldDisabled}>퇴사</button>
                                </div>
                                <div className="job-apply-v2-career-date-wrap">
                                  <input type="date" value={c.startDate} onChange={(e) => updateCareer(idx, 'startDate', e.target.value)} disabled={fieldDisabled} />
                                  <span className="job-apply-v2-career-date-sep">~</span>
                                  <input type="date" value={c.endDate} onChange={(e) => updateCareer(idx, 'endDate', e.target.value)} disabled={fieldDisabled || c.employmentStatus === 'current'} placeholder={c.employmentStatus === 'current' ? '' : '퇴사일'} />
                                </div>
                              </div>
                            </div>
                            <div className="job-apply-v2-career-row">
                              <label><span className="required">*</span> 담당업무</label>
                              <input type="text" value={c.assignedTasks} onChange={(e) => updateCareer(idx, 'assignedTasks', e.target.value)} placeholder="담당업무를 입력해주세요." className="job-apply-v2-career-input-full" disabled={fieldDisabled} />
                            </div>
                            <div className="job-apply-v2-career-row">
                              <label>퇴직사유</label>
                              <input type="text" value={c.leaveReason} onChange={(e) => updateCareer(idx, 'leaveReason', e.target.value)} placeholder="퇴직사유를 입력해주세요." className="job-apply-v2-career-input-full" disabled={fieldDisabled} />
                            </div>
                          </div>
                        </div>
                      ); })}
                      <button type="button" className="job-apply-v2-career-add" onClick={addCareer}>+ 운송경력 추가</button>
                    </div>
                  </div>
                )}

                {activeTab === 'license' && (
                  <div className="job-apply-v2-tab-panel">
                    <div className="job-apply-v2-career-header">
                      <h3 className="job-apply-v2-career-title">자격/기타</h3>
                      <span className="job-apply-v2-required-hint">* 필수항목</span>
                    </div>
                    <div className="job-apply-v2-tab-inner">
                      <div className="job-apply-v2-row">
                        <label><span className="required">*</span> 화물운송종사자격증</label>
                        <div className="job-apply-v2-segmented">
                          <button type="button" className={`job-apply-v2-segmented-btn ${formData.freightLicense === 'yes' ? 'active' : ''}`} onClick={() => setFormData({ ...formData, freightLicense: 'yes' })}>있음</button>
                          <button type="button" className={`job-apply-v2-segmented-btn ${formData.freightLicense === 'no' ? 'active' : ''}`} onClick={() => setFormData({ ...formData, freightLicense: 'no' })}>없음</button>
                        </div>
                      </div>
                      <div className="job-apply-v2-row">
                        <label><span className="required">*</span> 차량 소지 여부</label>
                        <div className="job-apply-v2-segmented">
                          <button type="button" className={`job-apply-v2-segmented-btn ${formData.hasVehicle === 'yes' ? 'active' : ''}`} onClick={() => setFormData({ ...formData, hasVehicle: 'yes' })}>있음</button>
                          <button type="button" className={`job-apply-v2-segmented-btn ${formData.hasVehicle === 'no' ? 'active' : ''}`} onClick={() => setFormData({ ...formData, hasVehicle: 'no', vehicleModel: '' })}>없음</button>
                        </div>
                      </div>
                      {formData.hasVehicle === 'yes' && (
                        <div className="job-apply-v2-row">
                          <label><span className="required">*</span> 소지 차량 차종</label>
                          <div className="job-apply-v2-input-wrap">
                            <input type="text" value={formData.vehicleModel} onChange={(e) => setFormData({ ...formData, vehicleModel: e.target.value })} placeholder="소지 차량 차종을 입력해주세요." />
                          </div>
                        </div>
                      )}
                      <div className="job-apply-v2-row">
                        <label><span className="required">*</span> 배넘버 여부</label>
                        <div className="job-apply-v2-segmented">
                          <button type="button" className={`job-apply-v2-segmented-btn ${formData.hasVehicleNumber === 'yes' ? 'active' : ''}`} onClick={() => setFormData({ ...formData, hasVehicleNumber: 'yes' })}>있음</button>
                          <button type="button" className={`job-apply-v2-segmented-btn ${formData.hasVehicleNumber === 'no' ? 'active' : ''}`} onClick={() => setFormData({ ...formData, hasVehicleNumber: 'no' })}>없음</button>
                        </div>
                      </div>
                      <div className="job-apply-v2-row">
                        <label>자격증 소유 여부</label>
                        <div className="job-apply-v2-input-wrap">
                          <textarea value={formData.license} onChange={(e) => setFormData({ ...formData, license: e.target.value })} placeholder="보유 자격증을 입력해주세요. (없으면 미기재)" rows={3} />
                        </div>
                      </div>
                      <div className="job-apply-v2-row">
                        <label>기타사항</label>
                        <div className="job-apply-v2-input-wrap">
                          <textarea value={formData.otherNotes} onChange={(e) => setFormData({ ...formData, otherNotes: e.target.value })} placeholder="기타사항을 입력해주세요." rows={4} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="job-apply-v2-footer">
                  <button type="button" className="job-apply-v2-btn-temp" onClick={handleTempSave}>
                    임시 저장
                  </button>
                  <button type="submit" className="job-apply-v2-btn-submit">
                    {activeTab === 'license' ? '지원서 제출' : '다음'}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>

      {showConsentModal && (
        <JobApplyConsentModal
          jobTitle={job?.title}
          onAgree={() => {
            setShowConsentModal(false);
            setFormData((prev) => ({ ...prev, privacyAgreed: true }));
          }}
          onDismiss={() => setShowConsentModal(false)}
        />
      )}
    </JobLayout>
  );
};

export default JobApply;
